import React, { useContext, useEffect, useState } from 'react'
import { Form } from 'reactstrap'

import { API } from 'API'
import { RequestToasterContext } from 'containers/Providers'
import { ALL_ACCESS_ROLES } from 'domains/user/UserAccessRoles'

interface AccessRolesControlProps {
  username: string
  accessRoles: string
  onUpdated?: () => void
}

export default function AccessRolesControl({
  username,
  accessRoles,
  onUpdated,
}: AccessRolesControlProps) {
  const { requestStatusRef } = useContext(RequestToasterContext)

  const parseRoles = (value: string | null | undefined): string[] => {
    const roleNames = (value ?? '')
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r !== '')

    return Array.from(new Set(roleNames)).sort()
  }

  const areEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false
    }
    return true
  }

  const [initialSelected, setInitialSelected] = useState<string[]>(parseRoles(accessRoles))
  const [selected, setSelected] = useState<string[]>(parseRoles(accessRoles))
  const isDirty = !areEqual(initialSelected, selected)

  useEffect(() => {
    const initial = parseRoles(accessRoles)

    setInitialSelected(initial)
    setSelected(initial)
  }, [accessRoles])

  const handleToggle = (role: string) => {
    setSelected((prev) => {
      const updated = prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]

      return Array.from(new Set(updated)).sort()
    })
  }

  const updateRoles = () => {
    API.updateAccessRoles(username, selected.join(','))
      .then((status) => {
        if (status === 200) {
          requestStatusRef.current?.showAlert('Updated', 'success')
          onUpdated?.()
        }
      })
      .catch(() => {
        requestStatusRef.current?.showAlert('Failed to update permissions', 'danger')
      })
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    if (!isDirty) return
    updateRoles()
  }

  const isButtonDisabled = !isDirty

  return (
    <Form onSubmit={handleSubmit}>
      <div
        className="p-2 d-flex flex-column"
        style={{
          borderRadius: 4,
          gap: '0.5rem',
        }}>
        <small style={{ fontSize: '0.8rem', color: '#888' }}>
          Selecting a role automatically configures access permissions across Spark applications.
        </small>
        {ALL_ACCESS_ROLES.map((role) => (
          <div key={role} className="form-check">
            <input
              type="checkbox"
              id={role}
              className="form-check-input"
              checked={selected.includes(role)}
              onChange={() => handleToggle(role)}
            />
            <label className="form-check-label" htmlFor={role}>
              {role}
            </label>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-end mt-3" style={{ gap: '1rem' }}>
        <button type="submit" disabled={isButtonDisabled}>
          Update Roles
        </button>
      </div>
    </Form>
  )
}
